import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity, ArrowLeft, RefreshCw, Share2, Loader, DoorOpen, Video, Phone,
  Search, FileText, Info, ExternalLink, Clock, User, Inbox, Tag,
  AlertTriangle, X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import ShareExportModal from '../components/investigations/ShareExportModal';
import RelatedAnalyticsEvents from '../components/investigations/RelatedAnalyticsEvents';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const TABS = [
  { id: 'evidence', label: 'Evidence', icon: Activity },
  { id: 'notes',    label: 'Notes',    icon: FileText },
  { id: 'analytics', label: 'Related events', icon: Activity },
  { id: 'details',  label: 'Details',  icon: Info },
];

const TYPE_META = {
  ACS_EVENT:    { icon: DoorOpen, color: '#5eead4', label: 'ACS Event',     filterLabel: 'ACS Events' },
  VMS_CLIP:     { icon: Video,    color: '#60a5fa', label: 'VMS Clip',      filterLabel: 'VMS Clips' },
  FR_MATCH:     { icon: Activity, color: '#a855f7', label: 'FR Match',      filterLabel: 'FR Matches' },
  INTERCOM_LOG: { icon: Phone,    color: '#fb923c', label: 'Intercom Log',  filterLabel: 'Intercom Logs' },
  MANUAL_NOTE:  { icon: FileText, color: '#94a3b8', label: 'Manual Note',   filterLabel: 'Notes' },
  UPLOADED_FILE:{ icon: FileText, color: '#94a3b8', label: 'Uploaded File', filterLabel: 'Files' },
};

const EVENT_TYPE_LABELS = {
  'access.granted':     'Access Granted',
  'access.denied':      'Access Denied',
  'access.forced':      'Door Forced',
  'access.held':        'Door Held',
  'access.tailgate':    'Tailgate Detected',
  'intercom.call':      'Intercom Call',
  'intercom.answered':  'Intercom Answered',
  'intercom.missed':    'Intercom Missed',
  'intercom.ended':     'Intercom Ended',
  'fr.match':           'Face Match',
  'fr.alert':           'Watchlist Alert',
  'fr.detection':       'Face Detected',
};

const TERMINAL_STATUSES = new Set(['COMPLETE', 'PARTIAL', 'FAILED']);

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function timeAgo(iso) {
  if (!iso) return '';
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function eventTypeLabel(t) {
  if (!t) return '';
  if (EVENT_TYPE_LABELS[t]) return EVENT_TYPE_LABELS[t];
  return String(t)
    .replace(/[._]+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function userInitial(name) {
  if (!name) return '?';
  const trimmed = String(name).trim();
  return (trimmed[0] || '?').toUpperCase();
}

/** Deterministic accent color for an avatar from a name. */
function colorFromName(name) {
  const palette = ['#60a5fa', '#a855f7', '#5eead4', '#fb923c', '#f472b6', '#facc15', '#34d399'];
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return palette[Math.abs(h) % palette.length];
}

// ─────────────────────────────────────────────
// STATUS CHIP
// ─────────────────────────────────────────────

function EvidenceStatusChip({ status }) {
  if (!status) return null;
  const isGathering = status === 'GATHERING';
  return (
    <span className={`inv-badge inv-badge-evidence-${status}`}>
      {isGathering && <Loader size={11} className="spin" />}
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────
// EVIDENCE TAB
// ─────────────────────────────────────────────

function EvidenceTab({ caseId, evidenceStatus, onRegather }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce text search.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadEvidence = useCallback(async () => {
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/evidence`);
      if (!r.ok) throw new Error('Failed to load evidence');
      const data = await r.json();
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load evidence');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadEvidence(); }, [loadEvidence]);

  // Refetch when gathering transitions to a terminal status.
  const prevStatusRef = useRef(evidenceStatus);
  useEffect(() => {
    if (prevStatusRef.current === 'GATHERING' && TERMINAL_STATUSES.has(evidenceStatus)) {
      loadEvidence();
    }
    prevStatusRef.current = evidenceStatus;
  }, [evidenceStatus, loadEvidence]);

  // Which type chips to show — only those with items.
  const typeCounts = useMemo(() => {
    const counts = {};
    for (const it of items) counts[it.type] = (counts[it.type] || 0) + 1;
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter(it => {
      if (typeFilter !== 'ALL' && it.type !== typeFilter) return false;
      if (!search) return true;
      const meta = it.metadata || {};
      const hay = `${it.deviceLabel || ''} ${meta.actorName || ''} ${it.frSubjectName || ''}`.toLowerCase();
      return hay.includes(search);
    });
  }, [items, typeFilter, search]);

  return (
    <>
      {evidenceStatus === 'GATHERING' && <div className="inv-gathering-bar" />}

      <div className="inv-filters">
        <div className="inv-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by device, actor, or subject…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>

        <div className="inv-severity-chips">
          <button
            className={`inv-chip ${typeFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setTypeFilter('ALL')}
          >
            All
            <span className="inv-chip-count">{items.length}</span>
          </button>
          {Object.keys(TYPE_META).filter(t => typeCounts[t] > 0).map(t => (
            <button
              key={t}
              className={`inv-chip ${typeFilter === t ? 'active' : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              {TYPE_META[t].filterLabel}
              <span className="inv-chip-count">{typeCounts[t]}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="inv-list-loading">
          <Loader size={20} className="spin" />
          <span>Loading evidence…</span>
        </div>
      ) : items.length === 0 ? (
        <div className="inv-evidence-empty">
          <Inbox size={40} />
          <div className="inv-empty-title">
            {evidenceStatus === 'GATHERING' ? 'Gathering evidence…' : 'No evidence gathered yet'}
          </div>
          <div className="inv-empty-sub">
            {evidenceStatus === 'GATHERING'
              ? 'Evidence will appear here as it is collected.'
              : 'Evidence from the selected devices and time window will appear here once gathering completes.'}
          </div>
          {(evidenceStatus === 'FAILED' || evidenceStatus === 'PARTIAL') && (
            <button className="btn-primary" onClick={onRegather}>
              <RefreshCw size={14} /> Re-gather Evidence
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="inv-evidence-empty">
          <Search size={36} />
          <div className="inv-empty-title">No matches</div>
          <div className="inv-empty-sub">Try a different filter or search term.</div>
        </div>
      ) : (
        <div className="inv-evidence-list">
          {filtered.map(item => <EvidenceRow key={item.id} item={item} />)}
        </div>
      )}
    </>
  );
}

function EvidenceRow({ item }) {
  const meta = item.metadata || {};
  const M = TYPE_META[item.type] || TYPE_META.MANUAL_NOTE;
  const Icon = M.icon;
  const rowClass = `inv-evidence-row inv-evidence-row-${item.type.toLowerCase().split('_')[0]}`;

  // FR_MATCH left-cluster overrides the default icon with a face thumb (or
  // initial-placeholder when Oosto hasn't returned an imageUrl).
  const isFr = item.type === 'FR_MATCH';
  const watchlists = Array.isArray(meta.watchlists) ? meta.watchlists : [];
  const enrollmentPhotoUrl = meta.enrollmentPhotoUrl || null;

  // Type-specific centerpiece content.
  let center = null;
  if (item.type === 'ACS_EVENT') {
    const evtLabel = meta.nativeType || eventTypeLabel(meta.eventType);
    center = (
      <>
        <span className="inv-evidence-actor">{meta.actorName || 'Unknown'}</span>
        {meta.actorType && <span className="inv-badge inv-badge-actor">{meta.actorType}</span>}
        {evtLabel && <span className="inv-evidence-evttype">{evtLabel}</span>}
      </>
    );
  } else if (item.type === 'VMS_CLIP') {
    const winMs = item.clipStart && item.clipEnd
      ? Math.round((new Date(item.clipEnd).getTime() - new Date(item.clipStart).getTime()) / 1000)
      : null;
    center = (
      <>
        <span className="inv-evidence-evttype">Footage clip</span>
        {winMs != null && (
          <span className="inv-evidence-sub">
            {new Date(item.clipStart).toLocaleTimeString()} → {new Date(item.clipEnd).toLocaleTimeString()} ({winMs}s)
          </span>
        )}
        {item.vmsClipUrl && (
          <a
            href={item.vmsClipUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inv-evidence-link"
            onClick={e => e.stopPropagation()}
          >
            View in VMS <ExternalLink size={11} />
          </a>
        )}
      </>
    );
  } else if (item.type === 'FR_MATCH') {
    const conf = typeof item.frConfidence === 'number'
      ? Math.round(item.frConfidence * (item.frConfidence > 1 ? 1 : 100))
      : null;
    const confClass = conf == null
      ? ''
      : conf >= 90 ? 'inv-confidence-high'
      : conf >= 70 ? 'inv-confidence-mid'
      : 'inv-confidence-low';
    center = (
      <>
        <span className="inv-evidence-actor">{item.frSubjectName || 'Unknown subject'}</span>
        {conf != null && (
          <span className={`inv-badge inv-badge-actor ${confClass}`}>{conf}% match</span>
        )}
        {watchlists.length > 0 && (
          <span
            className="inv-badge inv-badge-watchlist"
            title={watchlists.join(', ')}
          >
            WATCHLIST
          </span>
        )}
        {meta.eventType && <span className="inv-evidence-evttype">{eventTypeLabel(meta.eventType)}</span>}
        {enrollmentPhotoUrl && (
          <a
            href={enrollmentPhotoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inv-evidence-link"
            onClick={e => e.stopPropagation()}
          >
            View in Oosto <ExternalLink size={11} />
          </a>
        )}
      </>
    );
  } else if (item.type === 'INTERCOM_LOG') {
    center = (
      <>
        <span className="inv-evidence-evttype">{eventTypeLabel(meta.eventType) || 'Intercom event'}</span>
        {meta.callStatus && <span className="inv-evidence-sub">Status: {meta.callStatus}</span>}
        {meta.duration != null && <span className="inv-evidence-sub">{meta.duration}s</span>}
      </>
    );
  }

  const severity = meta.severity;

  // FR_MATCH: replace the default lucide icon with a 40×40 face thumb (or
  // initial-placeholder) so the operator can scan a long evidence list and
  // spot a known face immediately.
  const leftLeading = isFr
    ? (item.thumbnailUrl
        ? (
          <img
            className="inv-fr-thumb"
            src={item.thumbnailUrl}
            alt={item.frSubjectName || ''}
          />
        )
        : (
          <div className="inv-fr-thumb-placeholder" aria-hidden="true">
            {((item.frSubjectName || '?').trim().charAt(0) || '?').toUpperCase()}
          </div>
        ))
    : <Icon size={16} style={{ color: M.color, flexShrink: 0 }} />;

  return (
    <div className={rowClass} style={{ '--row-accent': M.color }}>
      <div className="inv-evidence-left">
        {leftLeading}
        <div className="inv-evidence-left-info">
          <span className="inv-evidence-device" title={item.deviceLabel}>
            {item.deviceLabel || item.deviceId || M.label}
          </span>
          {item.sourceSystem && (
            <span className="inv-badge inv-badge-system">{item.sourceSystem}</span>
          )}
        </div>
      </div>

      <div className="inv-evidence-center">{center}</div>

      <div className="inv-evidence-right">
        {severity && <span className={`inv-sev-dot inv-sev-${severity}`} title={severity} />}
        <span className="inv-evidence-time" title={formatDateTime(item.eventTimestamp)}>
          {item.eventTimestamp ? formatDateTime(item.eventTimestamp) : timeAgo(item.createdAt)}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// NOTES TAB
// ─────────────────────────────────────────────

function NotesTab({ caseId, notes, onNotesChanged }) {
  const [draft, setDraft]   = useState('');
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    const content = draft.trim();
    if (!content) return;
    setSaving(true);

    // Optimistic update: insert a placeholder while the request is in flight.
    const optimisticId = `tmp-${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      content,
      createdAt: new Date().toISOString(),
      authorUser: { name: 'You' },
      _optimistic: true,
    };
    onNotesChanged([optimistic, ...notes]);
    setDraft('');

    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${caseId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!r.ok) throw new Error('Failed to add note');
      const created = await r.json();
      onNotesChanged([created, ...notes]);
      toast.success('Note added');
    } catch {
      // Revert on failure.
      onNotesChanged(notes);
      setDraft(content);
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const sorted = [...(notes || [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <>
      <div className="inv-note-form">
        <textarea
          rows={3}
          placeholder="Add a note about this investigation…"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          disabled={saving}
        />
        <div className="inv-note-form-actions">
          <button
            className="btn-primary"
            onClick={addNote}
            disabled={saving || !draft.trim()}
          >
            {saving ? <Loader size={13} className="spin" /> : null}
            {saving ? 'Adding…' : 'Add Note'}
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="inv-evidence-empty">
          <FileText size={36} />
          <div className="inv-empty-title">No notes yet</div>
          <div className="inv-empty-sub">Use notes to document observations, decisions, and follow-ups.</div>
        </div>
      ) : (
        <div className="inv-notes-list">
          {sorted.map(n => {
            const authorName = n.authorUser?.name || 'Unknown';
            return (
              <div key={n.id} className={`inv-note-item ${n._optimistic ? 'inv-note-optimistic' : ''}`}>
                <div
                  className="inv-note-avatar"
                  style={{ background: colorFromName(authorName) }}
                  title={authorName}
                >
                  {userInitial(authorName)}
                </div>
                <div className="inv-note-body">
                  <div className="inv-note-meta">
                    <span className="inv-note-author">{authorName}</span>
                    <span className="inv-note-time" title={formatDateTime(n.createdAt)}>
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>
                  <div className="inv-note-content">{n.content}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// DETAILS TAB
// ─────────────────────────────────────────────

function DetailsTab({ caseData, onRegather, regathering }) {
  const ds = caseData.deviceSelections || {};
  const counts = caseData._count || {};
  const tags = caseData.tags || [];

  // Render device selections as concise rows.
  const selectionRows = [];
  if (ds.acs?.length > 0) {
    const door = ds.acsDoorIds?.length > 0
      ? ` (${ds.acsDoorIds.length} specific door${ds.acsDoorIds.length !== 1 ? 's' : ''})`
      : ' (all doors)';
    selectionRows.push({
      icon: DoorOpen, color: '#5eead4',
      label: `${ds.acs.length} ACS integration${ds.acs.length !== 1 ? 's' : ''}${door}`,
    });
  }
  if (ds.cameras?.length > 0) {
    selectionRows.push({
      icon: Video, color: '#60a5fa',
      label: `${ds.cameras.length} camera${ds.cameras.length !== 1 ? 's' : ''}`,
    });
  }
  if (ds.fr?.length > 0) {
    selectionRows.push({
      icon: Activity, color: '#a855f7',
      label: `${ds.fr.length} FR engine${ds.fr.length !== 1 ? 's' : ''}`,
    });
  }
  if (ds.intercoms?.length > 0) {
    selectionRows.push({
      icon: Phone, color: '#fb923c',
      label: `${ds.intercoms.length} intercom${ds.intercoms.length !== 1 ? 's' : ''}`,
    });
  }

  return (
    <div className="inv-details-grid">
      <section className="inv-details-card">
        <h3 className="inv-details-card-title">Case Metadata</h3>
        <DetailRow label="Created by" value={caseData.createdByUser?.name || '—'} />
        <DetailRow label="Opened at"  value={formatDateTime(caseData.openedAt)} />
        <DetailRow label="Assigned to" value={caseData.assignedToUser?.name || 'Unassigned'} />
        <DetailRow
          label="Tags"
          value={tags.length > 0
            ? <span className="inv-tags-row">
                {tags.map(t => <span key={t} className="inv-tag-chip"><Tag size={10} /> {t}</span>)}
              </span>
            : '—'}
        />
        {caseData.closedAt && <DetailRow label="Closed at" value={formatDateTime(caseData.closedAt)} />}
      </section>

      <section className="inv-details-card">
        <h3 className="inv-details-card-title">Incident Window</h3>
        <DetailRow label="Incident at" value={formatDateTime(caseData.incidentTimestamp)} />
        <DetailRow
          label="Window"
          value={caseData.incidentTimestamp ? `±${caseData.windowMinutes || 15} minutes` : '—'}
        />
        <DetailRow label="Description" value={caseData.description || '—'} />
      </section>

      <section className="inv-details-card">
        <h3 className="inv-details-card-title">Evidence Sources</h3>
        {selectionRows.length === 0 ? (
          <div className="inv-detail-muted">No device selections recorded for this case.</div>
        ) : (
          <ul className="inv-selection-list">
            {selectionRows.map((row, i) => {
              const Icon = row.icon;
              return (
                <li key={i}>
                  <Icon size={13} style={{ color: row.color }} /> {row.label}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="inv-details-card">
        <h3 className="inv-details-card-title">Gather Status</h3>
        <DetailRow
          label="Status"
          value={<EvidenceStatusChip status={caseData.evidenceStatus} />}
        />
        <DetailRow label="Items collected" value={counts.evidenceItems ?? 0} />
        <DetailRow label="Linked events"   value={counts.events ?? 0} />
        <DetailRow label="Last updated"    value={formatDateTime(caseData.updatedAt)} />
        <div style={{ marginTop: '0.75rem' }}>
          <button
            className="btn-primary"
            onClick={onRegather}
            disabled={regathering || !caseData.incidentTimestamp}
            title={!caseData.incidentTimestamp ? 'Case has no incident timestamp — cannot gather evidence' : ''}
          >
            {regathering ? <Loader size={13} className="spin" /> : <RefreshCw size={13} />}
            {regathering ? 'Triggering…' : 'Re-gather Evidence'}
          </button>
        </div>
      </section>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="inv-detail-row">
      <span className="inv-detail-label">{label}</span>
      <span className="inv-detail-value">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function InvestigationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [tab, setTab]           = useState('evidence');
  const [regathering, setRegathering] = useState(false);
  const [showShare, setShowShare]     = useState(false);
  // IQ staleness: validation state that changed after evidence was gathered.
  // Dismissal is per-mount (not persisted) — staleness may change on revisit.
  const [staleness, setStaleness]               = useState(null);
  const [stalenessDismissed, setStalenessDismissed] = useState(false);

  // Hold the polling timer so we can clear it on unmount or status change.
  const pollRef = useRef(null);

  const loadCase = useCallback(async () => {
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${id}`);
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load case (${r.status})`);
      }
      const data = await r.json();
      setCaseData(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load case');
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadCase(); }, [loadCase]);

  // IQ staleness check on case view. Non-fatal: any failure (no IQ, agent
  // down, error) leaves the banner hidden. Only surfaces when IQ confirms
  // one or more gathered items now have newer validation state.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    fetchWithTenant(`/api/iq/investigations/${id}/staleness`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        setStaleness(data?.ok && data.checked && data.stale > 0 ? data : null);
      })
      .catch(() => { if (!cancelled) setStaleness(null); });
    return () => { cancelled = true; };
  }, [id]);

  // Poll gather-status every 3s while gathering. The status response also
  // includes evidenceCount / byType which would be useful elsewhere, but for
  // the header chip we just need the status enum.
  useEffect(() => {
    const status = caseData?.evidenceStatus;
    if (status !== 'GATHERING') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    pollRef.current = setInterval(async () => {
      try {
        const r = await fetchWithTenant(`/api/investigations/cases/${id}/gather-status`);
        if (!r.ok) return;
        const data = await r.json();
        if (TERMINAL_STATUSES.has(data.evidenceStatus)) {
          // Terminal — refetch the full case to pick up updatedAt + counts.
          loadCase();
        } else {
          setCaseData(prev => prev ? { ...prev, evidenceStatus: data.evidenceStatus } : prev);
        }
      } catch {
        /* swallow — next tick will try again */
      }
    }, 3000);

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [caseData?.evidenceStatus, id, loadCase]);

  const handleRegather = useCallback(async () => {
    if (!caseData?.incidentTimestamp) {
      toast.warning('Case has no incident timestamp — cannot gather evidence');
      return;
    }
    setRegathering(true);
    try {
      const r = await fetchWithTenant(`/api/investigations/cases/${id}/gather`, { method: 'POST' });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to trigger re-gather');
      }
      // Flip local status to GATHERING immediately so the poll loop kicks in.
      setCaseData(prev => prev ? { ...prev, evidenceStatus: 'GATHERING' } : prev);
      toast.success('Re-gathering evidence…');
    } catch (err) {
      toast.error(err.message || 'Failed to trigger re-gather');
    } finally {
      setRegathering(false);
    }
  }, [id, caseData?.incidentTimestamp]);

  // ── RENDER ──

  if (loading) {
    return (
      <div className="inv-page inv-detail-page">
        <div className="inv-list-loading">
          <Loader size={20} className="spin" />
          <span>Loading case…</span>
        </div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="inv-page inv-detail-page">
        <div className="page-header-with-actions">
          <div className="page-header-content">
            <h1 className="page-title">
              <Activity size={22} />
              Case Not Found
            </h1>
          </div>
          <div className="page-header-actions">
            <button className="btn-secondary sm" onClick={() => navigate('/investigations')}>
              <ArrowLeft size={14} /> Back to Investigations
            </button>
          </div>
        </div>
        <div className="inv-evidence-empty">
          <Info size={40} />
          <div className="inv-empty-title">{error || 'Case not found'}</div>
        </div>
      </div>
    );
  }

  const handleNotesChanged = (newNotes) =>
    setCaseData(prev => prev ? { ...prev, notes: newNotes } : prev);

  return (
    <div className="inv-page inv-detail-page">
      {/* ── HEADER ── */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title">
            <Activity size={22} />
            <span className="inv-detail-title">{caseData.title}</span>
            <span className={`inv-badge inv-badge-${caseData.status}`}>
              {caseData.status.replace('_', ' ')}
            </span>
            <span className={`inv-badge inv-badge-${caseData.priority}`}>
              {caseData.priority}
            </span>
          </h1>
          <p className="page-subtitle">
            {caseData.incidentTimestamp ? (
              <>
                Incident: {formatDateTime(caseData.incidentTimestamp)} · ±{caseData.windowMinutes || 15} min window
              </>
            ) : (
              <>No incident timestamp set</>
            )}
          </p>
        </div>
        <div className="page-header-actions">
          <EvidenceStatusChip status={caseData.evidenceStatus} />
          <button
            className="btn-icon"
            title="Re-gather evidence"
            onClick={handleRegather}
            disabled={regathering || !caseData.incidentTimestamp}
          >
            {regathering
              ? <Loader size={16} className="spin" />
              : <RefreshCw size={16} />}
          </button>
          <button
            className="btn-icon"
            title="Share & Export"
            onClick={() => setShowShare(true)}
          >
            <Share2 size={16} />
          </button>
          <button className="btn-secondary sm" onClick={() => navigate('/investigations')}>
            <ArrowLeft size={14} /> Back to Investigations
          </button>
        </div>
      </div>

      {/* ── IQ STALENESS BANNER ── */}
      {staleness && !stalenessDismissed && (
        <div className="iq-staleness-banner">
          <div className="iq-staleness-banner-icon"><AlertTriangle size={18} /></div>
          <div className="iq-staleness-banner-body">
            <div className="iq-staleness-banner-title">
              IQ validation state changed after evidence was gathered
            </div>
            <div className="iq-staleness-banner-detail">
              {staleness.stale} of {staleness.total} IQ-sourced evidence items
              have updated validation state since this case was created. Review
              before exporting.
            </div>
            <details className="iq-staleness-banner-details">
              <summary>Show affected items ({staleness.items.length})</summary>
              <ul>
                {staleness.items.slice(0, 20).map(item => (
                  <li key={item.evidenceItemId}>
                    Detection {item.iqDetectionId}: gathered{' '}
                    {new Date(item.gatheredAt).toLocaleString()}, finalized{' '}
                    {new Date(item.finalizedAt).toLocaleString()}
                  </li>
                ))}
                {staleness.items.length > 20 && (
                  <li>…and {staleness.items.length - 20} more</li>
                )}
              </ul>
            </details>
          </div>
          <button
            className="iq-staleness-banner-dismiss"
            aria-label="Dismiss"
            onClick={() => setStalenessDismissed(true)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── TABS ── */}
      <div className="inv-tabs inv-detail-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = t.id === 'evidence'
            ? caseData._count?.evidenceItems
            : t.id === 'notes'
              ? (caseData.notes?.length ?? 0)
              : null;
          return (
            <button
              key={t.id}
              className={`inv-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <Icon size={14} /> {t.label}
              {typeof count === 'number' && count > 0 && (
                <span className="inv-tab-count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── TAB CONTENT ── */}
      {tab === 'evidence' && (
        <EvidenceTab
          caseId={id}
          evidenceStatus={caseData.evidenceStatus}
          onRegather={handleRegather}
        />
      )}
      {tab === 'notes' && (
        <NotesTab
          caseId={id}
          notes={caseData.notes || []}
          onNotesChanged={handleNotesChanged}
        />
      )}
      {tab === 'analytics' && (
        <RelatedAnalyticsEvents caseData={caseData} />
      )}
      {tab === 'details' && (
        <DetailsTab
          caseData={caseData}
          onRegather={handleRegather}
          regathering={regathering}
        />
      )}

      <ShareExportModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        caseId={id}
        evidenceCount={caseData._count?.evidenceItems ?? 0}
        notesCount={caseData.notes?.length ?? 0}
      />
    </div>
  );
}
